import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { BottomSheet } from '../ui/BottomSheet';

interface ConversationRichContentProps {
  html: string;
  className?: string;
}

/**
 * Renders sanitized conversation HTML and intercepts external links (data-external-url)
 * to show a warning: bottom sheet on mobile, centered modal on desktop.
 */
export function ConversationRichContent({ html, className = '' }: ConversationRichContentProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onClickCapture = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[data-external-url]') as HTMLAnchorElement | null;
      if (!anchor || !el.contains(anchor)) return;
      e.preventDefault();
      e.stopPropagation();
      const raw = anchor.getAttribute('data-external-url');
      if (!raw) return;
      let url: string;
      try {
        url = decodeURIComponent(raw);
      } catch {
        return;
      }
      try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return;
      } catch {
        return;
      }
      setPendingUrl(url);
    };

    el.addEventListener('click', onClickCapture, true);
    return () => el.removeEventListener('click', onClickCapture, true);
  }, [html]);

  const handleContinue = useCallback(() => {
    if (pendingUrl) {
      window.open(pendingUrl, '_blank', 'noopener,noreferrer');
    }
    setPendingUrl(null);
  }, [pendingUrl]);

  return (
    <>
      <div ref={containerRef} className={className} dangerouslySetInnerHTML={{ __html: html }} />

      <BottomSheet
        isOpen={!!pendingUrl}
        onClose={() => setPendingUrl(null)}
        title={t('conversations.externalLinkTitle')}
        desktopMode="modal"
        desktopMaxWidthClass="max-w-md"
        panelClassName="border-t border-gray-100 lg:border-0"
      >
        <div className="px-5 pb-6 pt-2 space-y-4">
          <div className="flex gap-3 rounded-xl border border-amber-200/80 bg-amber-50/90 p-4">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" aria-hidden />
            <p className="text-sm text-content/85 leading-relaxed">{t('conversations.externalLinkWarning')}</p>
          </div>

          <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2.5 flex items-start gap-2">
            <ExternalLink className="h-4 w-4 text-content/40 shrink-0 mt-0.5" aria-hidden />
            <p className="text-xs text-content/70 break-all font-mono leading-snug">{pendingUrl}</p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end pt-2">
            <button
              type="button"
              className="w-full sm:w-auto px-4 py-3 sm:py-2.5 rounded-xl border border-gray-200 text-content font-medium hover:bg-gray-50 transition-colors"
              onClick={() => setPendingUrl(null)}
            >
              {t('conversations.externalLinkCancel')}
            </button>
            <button
              type="button"
              className="w-full sm:w-auto px-4 py-3 sm:py-2.5 rounded-xl bg-accent-text text-white font-medium hover:bg-accent-text/90 transition-colors"
              onClick={handleContinue}
            >
              {t('conversations.externalLinkContinue')}
            </button>
          </div>
        </div>
      </BottomSheet>
    </>
  );
}
